import React from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Skeleton loader shown while calendar events are being fetched.
 * Mimics the month-grid layout of react-big-calendar so the page doesn't
 * collapse, plus a centered spinner so slow-device users have an obvious
 * "this is loading, not broken" signal.
 */
export const CalendarSkeletonLoader: React.FC = () => {
  const { t } = useTranslation()
  const weekdayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
  const totalCells = 42 // 6 weeks x 7 days

  return (
    <div
      className="relative"
      data-testid="calendar-skeleton-loader"
      aria-busy="true"
      aria-label="Loading calendar"
    >
      <div className="animate-pulse">
        {/* Toolbar skeleton: title + nav buttons */}
        <div className="flex items-center justify-between mb-4 px-2">
          <div className="h-6 bg-app-grey-20 rounded w-40"></div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-app-grey-20 rounded"></div>
            <div className="h-8 w-20 bg-app-grey-20 rounded"></div>
            <div className="h-8 w-8 bg-app-grey-20 rounded"></div>
            <div className="h-8 w-24 bg-app-grey-20 rounded ml-2"></div>
          </div>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 border-t border-l border-app-grey-30 rounded-t overflow-hidden">
          {weekdayLabels.map((label, i) => (
            <div
              key={`weekday-${i}`}
              className="h-8 flex items-center justify-center border-r border-b border-app-grey-30 bg-app-grey-10"
            >
              <span className="text-xs text-app-grey-40">{label}</span>
            </div>
          ))}
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-7 border-l border-app-grey-30 rounded-b overflow-hidden">
          {Array.from({ length: totalCells }).map((_, i) => (
            <div
              key={`cell-${i}`}
              className="h-20 sm:h-24 border-r border-b border-app-grey-30 p-2 flex flex-col gap-2"
            >
              {/* Date number placeholder */}
              <div className="h-3 w-5 bg-app-grey-20 rounded self-end"></div>
              {/* Event placeholders (shown on a subset of cells for realism) */}
              {i % 5 === 2 && <div className="h-3 bg-app-grey-20 rounded w-4/5"></div>}
              {i % 7 === 4 && <div className="h-3 bg-app-grey-20 rounded w-3/5"></div>}
            </div>
          ))}
        </div>
      </div>

      {/* Centered overlay with spinner + label so slow-device users see an
          unmistakable loading signal, not just gray bars. */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="flex flex-col items-center gap-2 bg-white/90 px-6 py-4 rounded-lg shadow-md">
          <Loader2 className="w-8 h-8 animate-spin text-primary-appBrand" />
          <span className="text-sm font-medium text-primary-text">
            {t('calendar.loadingCalendar')}
          </span>
        </div>
      </div>

      {/* Accessible loading text */}
      <span className="sr-only">{t('calendar.loadingCalendar')}</span>
    </div>
  )
}

export default CalendarSkeletonLoader
